import { SearchResult } from "../services/search";
import { ParsedRecord } from "../types/records";

export const getUniqueSerialNumbers = (
  results: SearchResult[],
  parseMetadata: (raw: any) => any
) => {
  const serialNumbers = new Set<string>();

  results.forEach((item: SearchResult) => {
    try {
      const metadata = parseMetadata(item.metadata_raw);
      // Check both direct serialNumber and deviceInfo.serialNumber
      const serialNumber =
        metadata.serialNumber ||
        (metadata.deviceInfo && metadata.deviceInfo.serialNumber);

      if (serialNumber) {
        serialNumbers.add(serialNumber);
      }
    } catch (error) {
      console.log("Error parsing metadata:", error);
    }
  });

  return Array.from(serialNumbers);
};

export const getUniqueDeviceTypes = (
  results: SearchResult[],
  parseMetadata: (raw: any) => any
) => {
  const deviceTypes = new Set<string>();

  results.forEach((item: SearchResult) => {
    try {
      const metadata = parseMetadata(item.metadata_raw);
      // Check both direct deviceType and deviceInfo.type
      const deviceType =
        metadata.deviceType ||
        (metadata.deviceInfo && metadata.deviceInfo.type);

      if (deviceType) {
        deviceTypes.add(deviceType);
      }
    } catch (error) {
      // Skip invalid metadata
    }
  });

  return Array.from(deviceTypes);
};

export const formatTimelineItems = (
  results: SearchResult[],
  parseMetadata: (raw: any) => any
) => {
  return results.map((result: SearchResult) => {
    const metadata = parseMetadata(result.metadata_raw);
    const id =
      typeof result.metadata_id === "object"
        ? result.metadata_id.low
        : result.metadata_id;

    // Extract values from either top-level or deviceInfo
    const deviceInfo = metadata.deviceInfo || {};
    const description = metadata.description || deviceInfo.description || "";

    // Get identifier from various possible locations
    const identifier =
      metadata.serialNumber ||
      deviceInfo.serialNumber ||
      metadata.serial ||
      deviceInfo.serial ||
      metadata.identifier ||
      deviceInfo.identifier;

    // Get manufacturer/make from various possible locations
    const manufacturer =
      metadata.manufacturer ||
      deviceInfo.manufacturer ||
      metadata.make ||
      deviceInfo.make ||
      "";

    // Get model from various possible locations
    const model = metadata.model || deviceInfo.model || "";

    // Build description parts
    const descriptionParts = [];

    if (manufacturer || model) {
      descriptionParts.push(`${manufacturer || ""} ${model || ""}`.trim());
    }

    if (identifier) {
      descriptionParts.push(identifier);
    }

    if (description) {
      descriptionParts.push(description);
    }

    return {
      id,
      title: metadata.title || deviceInfo.title || "Untitled",
      description: descriptionParts.join("\n"),
      timestamp:
        metadata.timestamp || metadata.date || new Date().toISOString(),
      metadata: {
        raw: result.metadata_raw,
        parsed: metadata,
        identifier: identifier, // Add identifier directly to metadata for click handler
      },
    };
  });
};

export const convertToRecords = (
  results: SearchResult[],
  parseMetadata: (raw: any) => any
): ParsedRecord[] => {
  return results.map((result: SearchResult) => {
    const metadata = parseMetadata(result.metadata_raw);

    // Handle both old and new metadata formats
    const deviceInfo = metadata.deviceInfo || {};

    // Get identifier from various possible locations
    const identifier =
      metadata.serialNumber ||
      deviceInfo.serialNumber ||
      metadata.serial ||
      deviceInfo.serial ||
      metadata.identifier ||
      deviceInfo.identifier ||
      null;

    // Extract image data if available in the metadata
    const imageData = metadata.image;

    // Create a copy of metadata for filtering
    const filteredMetadata = { ...metadata };

    // Remove specific fields
    delete filteredMetadata.image;
    delete filteredMetadata.deviceInfo;

    // Check for blockchain data
    const blockchainData = metadata.blockchainData || null;
    
    // Create a ParsedRecord object from the search result
    return {
      hash:
        result.metadata_hash ||
        result.dam_hash ||
        result.current_hash ||
        metadata.hash,
      image: imageData,
      deviceInfo: metadata.deviceInfo
        ? {
            ...deviceInfo,
            identifier: identifier,
            serialNumber: identifier,
          }
        : {
            ...filteredMetadata,
            identifier: identifier,
            model: metadata.model || deviceInfo.model || "",
            serialNumber: identifier,
          },
      timestamp:
        metadata.timestamp || metadata.date || new Date().toISOString(),
      // Include blockchain data if available
      blockchainData: blockchainData,
    };
  });
};

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return dateString;
  }
};

export const formatKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase());
};
